import { useEffect, useMemo, useState } from "react";

import { RootComponentInstance } from "@uniformdev/canvas";
import {
    RenderComponentResolver,
    UniformComposition,
    UniformPlayground
} from "@uniformdev/canvas-react";
import { Quirks } from "@uniformdev/context";
import { useUniformContext } from "@uniformdev/context-react";

import { expectContent } from "@/content/utils/expectContent";
import { FourOhFourPageTemplate } from "@/templates/404/404-page/src/404-page.template";
import {
    checkFeatureFlagGate,
    featureFlags as featureFlagsManifest
} from "@/utils/feature-flags";

import { enhanceComposition } from "../clients/uniform-client/enhancers";
import { useUserContext } from "../components/user-context";
import { parseRoute } from "../wac-routes";

import UNIFORM_DEFINITIONS from "./uniform-definitions";
import { UniformMissingComponent } from "./utils/uniform-missing-component";
import { useWAEContext } from "./utils/wae-context-provider";

declare global {
    interface Window {
        _uniformPreloadedComposition?: RootComponentInstance;
    }
}

export type UniformWrapperProps = {
    composition?: RootComponentInstance;
    isUniformPlayground?: boolean;
    isAllowPostMessage?: boolean;
};

const resolveRenderer: RenderComponentResolver = ({ type }) => {
    const MissingComponent = () => (
        <UniformMissingComponent componentName={type} />
    );
    return UNIFORM_DEFINITIONS[type] ?? MissingComponent;
};

export const UniformWrapper = ({
    composition,
    isUniformPlayground,
    isAllowPostMessage
}: UniformWrapperProps) => {
    const preloadedComposition =
        typeof window !== "undefined"
            ? window._uniformPreloadedComposition
            : null;

    const initialData = preloadedComposition || composition;

    const [baseComposition, setBaseComposition] = useState(initialData);
    const [personalizedComposition, setPersonalizedComposition] =
        useState(initialData);
    const [isFetched, setIsFetched] = useState(!!initialData);
    const [shouldRender404, setShouldRender404] = useState(false);

    // Listen for postMessage events to update composition dynamically
    // Only enabled if allowPostMessage flag is set and in non-production environments
    useEffect(() => {
        if (typeof window === "undefined") return;

        // Only enable if allowPostMessage flag is set
        if (!isAllowPostMessage) return;

        const handleMessage = async (event: MessageEvent) => {
            // Check if message is for updating Uniform composition
            if (
                event.data?.type === "UNIFORM_COMPOSITION_UPDATE" &&
                event.data?.composition
            ) {
                try {
                    const compositionData = event.data.composition;
                    // If composition is wrapped in compositionApiResponse, extract it
                    const rawComposition =
                        compositionData.compositionApiResponse?.composition ||
                        compositionData;

                    // Enhance the composition
                    const enhancedComp = await enhanceComposition(
                        rawComposition as RootComponentInstance
                    );

                    // Update state to trigger re-render
                    setBaseComposition(enhancedComp);
                    setIsFetched(true);

                    console.log("Uniform composition updated via postMessage");
                } catch (error) {
                    console.error(
                        "Error processing composition from postMessage:",
                        error
                    );
                }
            }
        };

        window.addEventListener("message", handleMessage);

        return () => {
            window.removeEventListener("message", handleMessage);
        };
    }, [isAllowPostMessage]);

    useEffect(() => {
        // When isAllowPostMessage is true and compositionId is in the URL,
        // we need to fetch from the Canvas API even if initialData exists
        // (because initialData may be EMPTY_COMPOSITION from SSR).
        const hasCompositionIdInUrl =
            typeof window !== "undefined" &&
            new URLSearchParams(window.location.search).has("compositionId");

        if (initialData && !(isAllowPostMessage && hasCompositionIdInUrl)) {
            return;
        }

        const addNoIndexTag = () => {
            if (typeof document !== "undefined") {
                let robotsMeta = document.querySelector('meta[name="robots"]');
                if (!robotsMeta) {
                    robotsMeta = document.createElement("meta");
                    robotsMeta.setAttribute("name", "robots");
                    document.head.appendChild(robotsMeta);
                }
                robotsMeta.setAttribute("content", "noindex,nofollow");
            }
        };

        const fetchFromCanvasApi = async (
            compositionId: string,
            projectId: string,
            apiKey: string,
            previewLocale: string
        ) => {
            const isLocalhost =
                window.location.hostname === "localhost" ||
                window.location.hostname === "127.0.0.1";

            const buildCanvasUrl = (state: string) =>
                isLocalhost
                    ? `/api/uniform-canvas-proxy?compositionId=${encodeURIComponent(
                          compositionId
                      )}&state=${state}&projectId=${encodeURIComponent(
                          projectId
                      )}&apiKey=${encodeURIComponent(
                          apiKey
                      )}&locale=${encodeURIComponent(previewLocale)}`
                    : `https://uniform.app/api/v1/canvas?compositionId=${encodeURIComponent(
                          compositionId
                      )}&state=${state}&projectId=${encodeURIComponent(
                          projectId
                      )}&withComponentIDs=true&locale=${encodeURIComponent(
                          previewLocale
                      )}`;
            const headers: HeadersInit = isLocalhost
                ? {}
                : { "x-api-key": apiKey, "x-project-id": projectId };

            const res = await fetch(buildCanvasUrl("64"), { headers });
            if (!res.ok) {
                // Fall back to draft state if published not found
                const draftRes = await fetch(buildCanvasUrl("0"), { headers });
                if (!draftRes.ok) {
                    console.error(`Canvas API error: ${draftRes.status}`);
                    setShouldRender404(true);
                    setIsFetched(true);
                    return;
                }
                const draftData = await draftRes.json();
                const enhancedComp = await enhanceComposition(
                    draftData.composition
                );
                setBaseComposition(enhancedComp);
                setShouldRender404(false);
                setIsFetched(true);
                console.log(
                    "uniform-preview: composition loaded from Canvas API (draft)"
                );
                return;
            }
            const data = await res.json();
            const enhancedComp = await enhanceComposition(data.composition);
            setBaseComposition(enhancedComp);
            setShouldRender404(false);
            setIsFetched(true);
            console.log(
                "uniform-preview: composition loaded from Canvas API (published)"
            );
        };

        // oxlint-disable-next-line complexity
        // eslint-disable-next-line complexity
        const fetchComposition = async () => {
            if (typeof window === "undefined") return;

            const { path, locale } = parseRoute(window.location);

            if (path === "uniform-preview" && isAllowPostMessage === true) {
                // Check for compositionId query param — if present, fetch directly
                // from the Uniform Canvas API instead of waiting for postMessage.
                // This is the preferred approach for migration validation as it
                // avoids the fragile postMessage/localStorage chunking flow.
                const params = new URLSearchParams(window.location.search);
                const compositionId = params.get("compositionId");
                const projectId = params.get("projectId");
                const apiKey = params.get("apiKey");

                if (compositionId && projectId && apiKey) {
                    // Get locale from query param or default to en-US
                    // This is required to resolve localized parameters correctly
                    const previewLocale = params.get("locale") || "en-US";

                    console.log(
                        "uniform-preview: fetching composition directly from Canvas API",
                        { compositionId, projectId, locale: previewLocale }
                    );
                    try {
                        await fetchFromCanvasApi(
                            compositionId,
                            projectId,
                            apiKey,
                            previewLocale
                        );
                    } catch (error) {
                        console.error(
                            "Error fetching composition from Canvas API:",
                            error
                        );
                        setShouldRender404(true);
                        setIsFetched(true);
                    }
                    return;
                }

                console.log(
                    "uniform-preview with allowPostMessage - waiting for postMessage"
                );
                return;
            }

            const slug = path.startsWith("/") ? path.substring(1) : path;

            const cdn = checkFeatureFlagGate(
                featureFlagsManifest.wacCompositionCutover
            )
                ? process.env["WAC_MANAGEMENT_CDN"]
                : process.env["UNIFORM_CDN"];
            const cdnPath = `release/${locale}/${slug}/composition.json`;

            const cdnComposition = `${cdn}${cdnPath.replace(/\/\//g, "/")}`;

            try {
                const req = await fetch(cdnComposition);
                setShouldRender404(req.status !== 200);
                if (req.status === 404) {
                    setIsFetched(true);
                    return;
                }
                const file = await req.json();
                const enhancedComp = await enhanceComposition(
                    file.compositionApiResponse.composition
                );
                setBaseComposition(enhancedComp);
                setShouldRender404(false);
                setIsFetched(true);

                if (enhancedComp?.parameters?.["noIndex"]?.value) {
                    addNoIndexTag();
                }
            } catch (error) {
                console.error("Error fetching composition from CDN:", error);
                setShouldRender404(true);
                setIsFetched(true);
            }
        };

        fetchComposition();
    }, [initialData, composition, isAllowPostMessage]);

    const { user } = useUserContext();
    const { context } = useUniformContext();
    const { personalizationValues } = useWAEContext();

    useMemo(() => {
        context.update({
            quirks: {
                ...personalizationValues
            }
        });
    }, [context, personalizationValues]);

    useEffect(() => {
        const tokens = new Map([["{{name}}", user?.name || ""]]);
        let jsonString = JSON.stringify(baseComposition);
        if (jsonString) {
            jsonString = jsonString.replace(/\{\{[a-zA-Z-]*\}\}/g, (match) => {
                return tokens.get(match) || match;
            });
            setPersonalizedComposition(JSON.parse(jsonString));
        }

        const updateQuirks = async () => {
            if (!user?.name) {
                return;
            }
            const quirks: Quirks = {
                ...context.quirks,
                name: user.name
            };
            await context.update({ quirks });
        };

        updateQuirks();
    }, [user?.name, context, baseComposition]);

    if (isUniformPlayground) {
        return (
            <UniformPlayground
                behaviorTracking="onLoad"
                resolveRenderer={resolveRenderer}
                contextualEditingEnhancer={({ composition }) =>
                    enhanceComposition(composition)
                }
            />
        );
    }

    return shouldRender404 ? (
        <FourOhFourPageTemplate />
    ) : (
        isFetched && (
            <div>
                <UniformComposition
                    data={expectContent(
                        (personalizedComposition ||
                            baseComposition) as RootComponentInstance
                    )}
                    behaviorTracking="onLoad"
                    resolveRenderer={resolveRenderer}
                    contextualEditingEnhancer={({ composition }) =>
                        enhanceComposition(composition)
                    }
                />
            </div>
        )
    );
};
